export type ValidationResult<T> = {
  isValid: boolean;
  value: T;
  error?: string;
};

export type PasswordValidationResult = ValidationResult<string> & {
  strengthScore: number;  // 0-4 scale
  requirements: {
    minLength: boolean;
    hasUpperCase: boolean;
    hasLowerCase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
};

export const validateLoginId = (loginId: string): ValidationResult<string> => {
  if (loginId.length < 6) {
    return {
      isValid: false,
      value: loginId,
      error: 'Login ID must be at least 6 characters long'
    };
  }
  return {
    isValid: true,
    value: loginId,
    error: ''
  };
};

export const validatePasswordMatch = (password: string, confirmPassword: string): ValidationResult<string> => {
  //console.log('===validatePasswordMatch called====, password:', password, '  |  ', confirmPassword);
  if (password !== confirmPassword) {
    return {
      isValid: false,
      value: confirmPassword,
      error: 'Passwords do not match'
    };
  }
  return {
    isValid: true,
    value: confirmPassword,
    error: ''  // Explicitly set empty string for valid case
  };
};

export const validatePassword = (password: string): PasswordValidationResult => {
  // Common password requirements
  const MIN_LENGTH = 8;
  const MAX_LENGTH = 128;
  
  const requirements = {
    minLength: password.length >= MIN_LENGTH,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  // Calculate strength score (0-4)
  let strengthScore = 0;
  if (requirements.minLength) strengthScore++;
  if (requirements.hasUpperCase && requirements.hasLowerCase) strengthScore++;
  if (requirements.hasNumber) strengthScore++;
  if (requirements.hasSpecialChar) strengthScore++;

  // Common weak passwords check
  const commonWeakPasswords = [
    'password', 'password123', '123456', 'qwerty',
    'abc123', 'letmein', 'admin', 'welcome'
  ];
  
  // Build error message based on failed requirements
  const errorMessages: string[] = [''];
  
  if (!requirements.minLength) {
    errorMessages.push(`must be at least ${MIN_LENGTH} characters long`);
  }
  if (!requirements.hasUpperCase) {
    errorMessages.push('must contain at least one uppercase letter');
  }
  if (!requirements.hasLowerCase) {
    errorMessages.push('must contain at least one lowercase letter');
  }
  if (!requirements.hasNumber) {
    errorMessages.push('must contain at least one number');
  }
  if (!requirements.hasSpecialChar) {
    errorMessages.push('must contain at least one special character');
  }
  if (password.length > MAX_LENGTH) {
    errorMessages.push(`Password cannot exceed ${MAX_LENGTH} characters`);
  }
  if (commonWeakPasswords.includes(password.toLowerCase())) {
    errorMessages.push('This password is too common. Please choose a stronger password');
  }

  // Check for sequential characters
  //if (/abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz/i.test(password)) {
  //  errorMessages.push('Password contains sequential letters');
  //}
  //if (/012|123|234|345|456|567|678|789/.test(password)) {
  //  errorMessages.push('Password contains sequential numbers');
  //}

const filteredErrors = errorMessages.filter(msg => msg.length > 0);
const isValid = filteredErrors.length === 0;
//console.log('===isValid====', isValid);
  //const isValid = errorMessages.length === 0;
  return {
    isValid,
    value: password,
    error: isValid ? '' : 'Password ' + errorMessages.join(', '),
    strengthScore,
    requirements
  };
};

// emailValidator.ts
export const validateEmail = (email: string): ValidationResult<string> => {
  const trimmedEmail = email.trim().toLowerCase();
  
  // Comprehensive email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!trimmedEmail) {
    return {
      isValid: false,
      value: email,
      error: 'Email is required'
    };
  }

  if (!emailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      value: email,
      error: 'Please enter a valid email address'
    };
  }

  return {
    isValid: true,
    value: trimmedEmail
  };
};

// phoneValidator.ts
export type PhoneFormat = 'dashed' | 'dotted' | 'parentheses' | 'plain';

export const validateAndFormatPhoneNumber = (
  phone: string,
  format: PhoneFormat = 'dashed'
): ValidationResult<string> => {
  // Remove all non-digit characters (including the + sign)
  let cleaned = phone.replace(/\D/g, '');
  
  if (!cleaned) {
    return {
      isValid: false,
      value: phone,
      error: 'Phone number is required'
    };
  }
  
  // If 11 digits and starts with 1, remove the leading 1 (US country code)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    cleaned = cleaned.slice(1);
  }
  
  // Now validate it's exactly 10 digits
  if (cleaned.length !== 10) {
    return {
      isValid: false,
      value: phone,
      error: 'Phone number must be 10 digits'
    };
  }
  
  const formatters = {
    dashed: (n: string) => `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`,
    dotted: (n: string) => `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`,
    parentheses: (n: string) => `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`,
    plain: (n: string) => n,
  };
  
  return {
    isValid: true,
    value: formatters[format](cleaned)
  };
};

// zipCodeValidator.ts
export const validateZipCode = (
  zipCode: string,
  allowPlusFour: boolean = false
): ValidationResult<string> => {
  const cleaned = zipCode.replace(/[^\d-]/g, '');
  
  if (!cleaned) {
    return {
      isValid: false,
      value: zipCode,
      error: 'ZIP code is required'
    };
  }

  // Basic 5-digit zip
  if (/^\d{5}$/.test(cleaned)) {
    return {
      isValid: true,
      value: cleaned
    };
  }

  // ZIP+4 if allowed
  if (allowPlusFour && /^\d{5}-?\d{4}$/.test(cleaned)) {
    return {
      isValid: true,
      value: cleaned.replace(/^(\d{5})(\d{4})$/, '$1-$2')
    };
  }

  return {
    isValid: false,
    value: zipCode,
    error: allowPlusFour 
      ? 'Please enter a 5-digit ZIP code or ZIP+4'
      : 'Please enter a 5-digit ZIP code'
  };
};
  
  export const isFieldRequired = (value: string) => 
    value.trim() !== '' ? undefined : 'This field is required';
  
  export const minFieldLength = (min: number) => (value: string) =>
    value.length >= min ? undefined : `Must be at least ${min} characters`;
  
  // validators/numberValidators.ts
  export const isNumberPositive = (value: number) =>
    value > 0 ? undefined : 'Must be a positive number';
  

  
